'use client';

import React from 'react';
import classnames from 'classnames';
import Box from '../../atoms/Box/Box';
import Typography from '../../atoms/Typography/Typography';
import Iconography from '../../atoms/Iconography/Iconography';
import ProgressBar from '../../atoms/ProgressBar/ProgressBar';

import styles from './Stepper.module.scss';

export type StepStatus = 'completed' | 'active' | 'pending';

export type Step = {
  /** Unique identifier for the step */
  id: string;
  /** Label displayed below the step indicator */
  label: string;
  /** Status of the step */
  status: StepStatus;
  /** Whether the label should be highlighted (e.g., for terminal states like "Closed") */
  highlighted?: boolean;
  /** Whether the step is disabled (not clickable) */
  disabled?: boolean;
};

export type StepperProps = {
  /** Array of steps to display */
  steps: Step[];
  /** Theme variant */
  theme?: 'dark' | 'light';
  /** Callback when a step is clicked */
  onStepClick?: (step: Step, index: number) => void;
  /** Whether the stepper is interactive (clickable) */
  interactive?: boolean;
  /** Test ID for testing purposes */
  dataTestId?: string;
  /** Additional CSS class */
  className?: string;
};

/**
 * Stepper component displays a horizontal progress indicator with multiple steps.
 * Each step can be in one of three states: completed, active, or pending.
 *
 * @component
 * @param {StepperProps} props - The component props
 * @returns {JSX.Element} The rendered Stepper component
 * @example
 * <Stepper
 *   steps={[
 *     { id: '1', label: 'Open', status: 'completed' },
 *     { id: '2', label: 'Contacting', status: 'completed' },
 *     { id: '3', label: 'Engaged', status: 'active' },
 *     { id: '4', label: 'Quote Generated', status: 'pending' },
 *   ]}
 *   theme="light"
 * />
 */
const Stepper: React.FC<StepperProps> = ({
  steps,
  theme = 'light',
  onStepClick,
  interactive = false,
  dataTestId = 'stepper',
  className,
}) => {
  const handleStepClick = (step: Step, index: number) => {
    if (interactive && onStepClick) {
      onStepClick(step, index);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, step: Step, index: number) => {
    if (interactive && onStepClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onStepClick(step, index);
    }
  };

  return (
    <Box
      className={classnames(styles.stepper, styles[theme], className)}
      testId={dataTestId}
      role="navigation"
      aria-label="Progress steps"
    >
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const isClickable = interactive && !step.disabled;

        return (
          <Box
            key={step.id}
            className={classnames(styles.stepContainer, {
              [styles.interactive]: isClickable,
              [styles.lastStepContainer]: isLast,
            })}
          >
            {/* Icon */}
            <Box
              className={classnames(styles.indicator, styles[step.status], {
                [styles.clickableIndicator]: isClickable,
              })}
              onClick={() => handleStepClick(step, index)}
              onKeyDown={(e) => handleKeyDown(e, step, index)}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              aria-current={step.status === 'active' ? 'step' : undefined}
              testId={`${dataTestId}-step-${step.id}`}
            >
              {step.status === 'completed' ? (
                <Box className={styles.completedIcon}>
                  <Iconography
                    category="global"
                    name="checkmark"
                    color={theme === 'dark' ? 'black' : 'white'}
                  />
                </Box>
              ) : step.status === 'active' ? (
                <Box className={styles.activeIcon}>
                  <Box className={styles.activeDot} />
                </Box>
              ) : (
                <Box className={styles.pendingIcon} />
              )}
            </Box>

            {/* Connector Line */}
            {!isLast && (
              <ProgressBar
                dataTestId={`${dataTestId}-connector-${step.id}`}
                value={step.status === 'pending' ? 0 : 100}
                theme={theme}
                size="small"
                variant={step.status === 'completed' ? 'black' : 'default'}
                className={classnames(styles.connector, {
                  [styles.pendingConnector]: step.status === 'pending',
                })}
              />
            )}

            {/* Label */}
            <Typography
              variant="body"
              size="small"
              theme={theme}
              color="white"
              className={classnames(styles.label, {
                [styles.activeLabel]: step.status === 'active',
                [styles.highlightedLabel]: step.highlighted,
                [styles.clickable]: isClickable,
                [styles.pending]: step.status === 'pending' && !step.highlighted,
              })}
              onClick={() => handleStepClick(step, index)}
              onKeyDown={(e) => handleKeyDown(e, step, index)}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
            >
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

Stepper.displayName = 'Stepper';

export default Stepper;
